import { createGlobalStore } from "@/stores/global"
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import i18n from "@/i18n"
import { ApiErrorResponse } from "@/app/api/type"

// API v1 에러 코드 -> 번역 키 매핑 (임시)
const ERROR_CODE_MAPPING: Record<string, string> = {
  DOES_NOT_EXIST_ACCOUNT: "201",
  DOES_NOT_EXIST_TEAM: "205",
  DOES_NOT_EXIST_COACH: "206",
  DOES_NOT_EXIST_PLAYER: "213",
  DOES_NOT_EXIST_ACTIVITY: "220",
  PLAYER_GROUPS_EXCEED_LIMIT: "303",
  INTERNAL_SERVER_ERROR: "500",
  SESSION_COUNT_EXCEEDED: "611",
  CANNOT_SHRINK_ZONE: "612",
  PASSWORD_MISMATCH: "906",
  EMAIL_NOT_VERIFIED: "909",
  AUTH_CODE_NOT_VERIFIED: "911",
  AUTH_CODE_NOT_FOUND: "918",
  FILE_EXTRACTION_FAILED: "920",
}

function makeQueryClient() {
  const { t } = i18n
  const { setValue: setSnackBar } = createGlobalStore((state) => ({
    setValue: state.setSnackBar,
  }))

  const handleError = (error: unknown, query?: { queryKey: readonly unknown[] }) => {
    // 공지사항 API 에러는 토스트 표시하지 않음 (Join 플로우 등에서 타이밍 이슈 방지)
    if (query?.queryKey?.includes("readLatestNotification")) {
      return
    }

    const errorResponse = error as ApiErrorResponse
    const apiError = errorResponse.error

    const errorCode = String(apiError?.code || apiError?.errorCode || "")

    // 경기장 없음 에러는 토스트 표시하지 않음
    if (errorCode === "DOES_NOT_EXIST_STADIUM") {
      return
    }

    const translationKey = ERROR_CODE_MAPPING[errorCode] || errorCode

    setSnackBar({
      open: true,
      message: t(`messages.${translationKey}`, {
        defaultValue: apiError?.message || apiError?.errorMsg || t("messages.failBasic"),
      }),
      severity: "error",
    })
  }

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0, // 캐시를 즉시 stale 상태로 만들어 항상 refetch
        gcTime: 0, // 캐시를 즉시 가비지 컬렉션하여 메모리에서 제거
        refetchOnMount: true, // 컴포넌트 마운트 시 항상 refetch
        refetchOnWindowFocus: false, // 윈도우 포커스 시 refetch 비활성화 (선택사항)
        retry: 0,
      },
    },

    queryCache: new QueryCache({
      onError: handleError,
    }),
    mutationCache: new MutationCache({
      onError: handleError,
    }),
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (typeof window === "undefined") {
    // Server Side일 경우 새로운 queryClient 를 생성
    return makeQueryClient()
  } else {
    // Client Side일 경우 browserQueryClient가 존재하지 않는 경우에만 새로운 queryClient 생성
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient()
    }

    return browserQueryClient
  }
}

export default function QueryProvider({ children }: React.PropsWithChildren) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
